import { TourModel, useCloseTour } from 'api-hooks/tour/tour-list/tour-detail';
import { CheckCircleSVG } from 'common/assets';
import { AuthorizationRules } from 'common/constants';
import { TourStatusEnum } from 'common/constants/enum';
import notification from 'components/common/notification';
import Button from 'components/elements/buttons/button';
import { PopoverMenuType } from 'components/popover';
import useApprovalAction from 'hooks/use-approval-action';
import { useAuthorization } from 'hooks/use-authorization';
import useDialog from 'hooks/use-dialog';
import useDismiss from 'hooks/use-dismiss';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

export default function useCloseTourAction(tour: TourModel) {
  const { t } = useTranslation();
  const closeMutate = useCloseTour();
  const dialog = useDialog();
  const { can } = useAuthorization();
  const dismiss = useDismiss('tours');
  const { onRequestApproval } = useApprovalAction('tours', tour);
  const status = tour.status;
  const id = tour.id;

  const isAllowed = React.useMemo(() => {
    switch (status) {
      case TourStatusEnum.Active:
        return true;
      default:
        return false;
    }
  }, [status]);

  const closePermission = can(AuthorizationRules.ToursClose) && isAllowed;

  const onCloseTour = React.useCallback(() => {
    dialog.showConfirmation({
      message: t('common:do_you_want_to_close_this_tour'),
      title: t('common:confirmation'),
      onPositiveAction: async (onClose) => {
        try {
          const result = await closeMutate.mutateAsync({
            tourId: id,
          });
          dismiss(id);
          notification.success({ message: result?.message });
          onClose();

          onRequestApproval(id);
        } catch (error) {
          notification.error({ message: error.message });
        }
      },
      onNegativeAction: (onClose) => {
        onClose();
      },
    });
  }, [closeMutate, dialog, dismiss, id, onRequestApproval, t]);

  const closeComponent = closePermission && (
    <Button
      startEnhancer={(color, size) => (
        <CheckCircleSVG color={color} size={size} />
      )}
      onClick={onCloseTour}
      isLoading={closeMutate.isLoading}
    >
      {t('common:close_tour')}
    </Button>
  );

  const closeAction: PopoverMenuType = {
    isAllowed: closePermission,
    key: `close-${id}`,
    label: t('common:close_tour'),
    onClick: onCloseTour,
    isLoading: closeMutate.isLoading,
  };

  return {
    closeMutate,
    closePermission,
    onCloseTour,
    closeComponent,
    closeAction,
  };
}
